/**
 * OutstandingPurchasesBadge — indicateur global "Reste dû fournisseurs".
 *
 * Affiche un badge cliquable dans le header dès qu'il y a au moins une
 * facture fournisseur échue non payée. Redirige vers HistoriqueAchats
 * pré-filtré sur les factures à régler (status=validated).
 *
 * - Polling léger (60s) pour rester synchronisé avec les ajouts de paiements.
 * - Silencieux si count=0 (pas de bruit visuel quand la trésorerie est saine).
 * - Feature-gated : n'apparaît que pour les users ayant `expenses.read`.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const fmtEUR = (v) => new Intl.NumberFormat("fr-FR", {
  style: "currency", currency: "EUR", minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}).format(v || 0);

export default function OutstandingPurchasesBadge() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canRead = hasPermission("expenses.read");
  const [state, setState] = useState({ count: 0, remaining: 0 });

  useEffect(() => {
    if (!canRead) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/supplier-invoices/payment-gaps");
        if (!cancelled) {
          setState({ count: data.count || 0, remaining: data.total_remaining_ttc || 0 });
        }
      } catch { /* silent — pas grave si l'endpoint est temporairement KO */ }
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [canRead]);

  if (!canRead || state.count === 0) return null;

  const critical = state.remaining >= 5000;

  return (
    <button
      onClick={() => navigate("/app/purchases/history?status=validated")}
      data-testid="outstanding-purchases-badge"
      title={`${state.count} facture(s) échue(s) — cliquer pour voir l'historique`}
      className={cn(
        "hidden sm:flex items-center gap-2 rounded-full pl-2.5 pr-3 py-1.5 border transition-all",
        "hover:shadow-sm hover:scale-[1.02]",
        critical
          ? "bg-red-50 border-red-200 text-red-800"
          : "bg-amber-50 border-amber-200 text-amber-800",
      )}
    >
      <AlertTriangle className={cn("h-3.5 w-3.5", critical ? "text-red-600" : "text-amber-600")} />
      <span className="text-[10px] uppercase tracking-widest">Reste dû</span>
      <span className="text-sm font-mono font-semibold tabular-nums"
             data-testid="outstanding-purchases-amount">
        {fmtEUR(state.remaining)}
      </span>
      <span className={cn(
        "text-[10px] font-mono px-1.5 py-0.5 rounded-full",
        critical ? "bg-red-100" : "bg-amber-100",
      )}>
        {state.count}
      </span>
    </button>
  );
}
